import { api } from "./client";
import { unwrapApiData } from "./unwrap";

export interface MonthlySalesTrendPoint {
  month: string;
  totalMinor: number;
}

export interface InventoryDistributionBucket {
  itemType: number;
  itemCount: number;
  totalValueMinor: number;
}

export interface DashboardKpis {
  orgId: string;
  startDate: string;
  endDate: string;
  totalSalesMinor: number;
  totalExpensesMinor: number;
  pendingWorkOrdersCount: number;
  activeEmployeesCount: number;
  cashBalanceMinor: number;
  bankBalanceMinor: number;
  receivablesMinor: number;
  payablesMinor: number;
  monthlySalesTrend: MonthlySalesTrendPoint[];
  inventoryDistribution: InventoryDistributionBucket[];
}

export interface AiInsight {
  insightId: string;
  orgId: string;
  generatedAt: string;
  contextSnapshotJson: string;
  aiResponseMarkdown: string;
  isActioned: number;
}

export interface GenerateAiInsightResult {
  insightId: string;
  orgId: string;
  generatedAt: string;
  aiResponseMarkdown: string;
  contextSnapshot: DashboardKpis;
  model: string;
}

interface DashboardKpisRow {
  orgId: string;
  startDate: string;
  endDate: string;
  totalSalesMinor: number;
  totalExpensesMinor: number;
  pendingWorkOrdersCount: number;
  activeEmployeesCount: number;
  cashBalanceMinor: number;
  bankBalanceMinor: number;
  receivablesMinor: number;
  payablesMinor: number;
  monthlySalesTrend: MonthlySalesTrendPoint[];
  inventoryDistribution: InventoryDistributionBucket[];
}

interface AiInsightRow {
  insightId: string;
  orgId: string;
  generatedAt: string;
  contextSnapshotJson: string;
  aiResponseMarkdown: string;
  isActioned: number;
}

interface GenerateAiInsightRow {
  insightId: string;
  orgId: string;
  generatedAt: string;
  aiResponseMarkdown: string;
  contextSnapshot: DashboardKpisRow;
  model: string;
}

function buildQuery(params: Record<string, string>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.trim()) {
      search.set(key, value.trim());
    }
  }
  const queryString = search.toString();
  return queryString ? `?${queryString}` : "";
}

function mapKpis(row: DashboardKpisRow): DashboardKpis {
  return {
    orgId: row.orgId,
    startDate: row.startDate,
    endDate: row.endDate,
    totalSalesMinor: row.totalSalesMinor,
    totalExpensesMinor: row.totalExpensesMinor,
    pendingWorkOrdersCount: row.pendingWorkOrdersCount,
    activeEmployeesCount: row.activeEmployeesCount,
    cashBalanceMinor: row.cashBalanceMinor,
    bankBalanceMinor: row.bankBalanceMinor,
    receivablesMinor: row.receivablesMinor,
    payablesMinor: row.payablesMinor,
    monthlySalesTrend: row.monthlySalesTrend ?? [],
    inventoryDistribution: row.inventoryDistribution ?? [],
  };
}

function mapInsight(row: AiInsightRow): AiInsight {
  return {
    insightId: row.insightId,
    orgId: row.orgId,
    generatedAt: row.generatedAt,
    contextSnapshotJson: row.contextSnapshotJson,
    aiResponseMarkdown: row.aiResponseMarkdown,
    isActioned: row.isActioned,
  };
}

export async function getKpis(
  startDate: string,
  endDate: string,
): Promise<DashboardKpis> {
  const start = startDate.trim();
  const end = endDate.trim();
  if (!start) {
    throw new Error("startDate is required");
  }
  if (!end) {
    throw new Error("endDate is required");
  }

  const query = buildQuery({ startDate: start, endDate: end });
  const res = await api.get<
    { ok: true; data: DashboardKpisRow } | import("./types").ApiErrorBody
  >(`/v1/dashboard/kpis${query}`);

  return mapKpis(unwrapApiData(res));
}

export async function getLatestAiInsight(): Promise<AiInsight | null> {
  const res = await api.get<
    | { ok: true; data: AiInsightRow | null; meta?: Record<string, unknown> }
    | import("./types").ApiErrorBody
  >("/v1/dashboard/ai-insight/latest");

  const payload = unwrapApiData(res);
  return payload ? mapInsight(payload) : null;
}

export async function generateAiInsight(
  startDate: string,
  endDate: string,
): Promise<GenerateAiInsightResult> {
  const start = startDate.trim();
  const end = endDate.trim();
  if (!start) {
    throw new Error("startDate is required");
  }
  if (!end) {
    throw new Error("endDate is required");
  }

  const res = await api.post<
    { ok: true; data: GenerateAiInsightRow } | import("./types").ApiErrorBody
  >("/v1/dashboard/ai-insight", {
    startDate: start,
    endDate: end,
  });

  const row = unwrapApiData(res);
  return {
    insightId: row.insightId,
    orgId: row.orgId,
    generatedAt: row.generatedAt,
    aiResponseMarkdown: row.aiResponseMarkdown,
    contextSnapshot: mapKpis(row.contextSnapshot),
    model: row.model,
  };
}
